import { useMemo, Children, isValidElement, type ReactElement, type ReactNode } from 'react';
import styled from '@emotion/styled';
import { DataGrid, type GridColDef, GridToolbar } from '@mui/x-data-grid';

type ScrollableTableProps = {
  children: React.ReactNode;
};

function extractText(node: ReactNode): string {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (Array.isArray(node)) return (node as ReactNode[]).map(extractText).join('');
  if (!isValidElement(node)) return '';
  const childContent = (node.props as { children?: ReactNode }).children;
  return extractText(childContent as ReactNode);
}

export const ScrollableTable = ({ children }: ScrollableTableProps) => {
  // Helper to check if element type matches (handles both native elements and styled components)
  const isElementType = (element: ReactElement, typeName: string): boolean => {
    if (element.type === typeName) return true;
    if (typeof element.type === 'function') {
      const displayName = (element.type as any).displayName || (element.type as any).name || '';
      return displayName.toLowerCase().includes(typeName.toLowerCase());
    }
    return false;
  };

  // Helper to extract header cells from a row
  const extractHeaderCells = (cells: React.ReactNode[]): string[] => {
    return cells
      .filter(
        (c): c is ReactElement => isValidElement(c) && (isElementType(c, 'th') || isElementType(c, 'td'))
      )
      .map((cell) => {
        const content = (cell.props as { children?: React.ReactNode }).children;
        return extractText(content as ReactNode);
      });
  };

  // Helper to check if a row is a header row
  const isHeaderRow = (cells: React.ReactNode[]): boolean => {
    const firstCellType = cells.find((c): c is ReactElement => isValidElement(c))?.type;

    if (firstCellType === 'th') {
      return true;
    }

    if (firstCellType && typeof firstCellType === 'function') {
      const displayName = (firstCellType as any).displayName || (firstCellType as any).name || '';
      return displayName.toLowerCase().includes('th');
    }

    return false;
  };

  // Helper to process thead elements
  const processThead = (child: ReactElement): string[] => {
    let headerCells: string[] = [];
    const theadChildren = Children.toArray((child.props as { children?: React.ReactNode }).children);

    theadChildren.forEach((tr) => {
      if (isValidElement(tr) && isElementType(tr, 'tr')) {
        const cells = Children.toArray((tr.props as { children?: React.ReactNode }).children);
        headerCells = extractHeaderCells(cells);
      }
    });

    return headerCells;
  };

  // Helper to process tbody elements
  const processTbody = (child: ReactElement): ReactElement[] => {
    const tbodyChildren = Children.toArray((child.props as { children?: React.ReactNode }).children);
    return tbodyChildren.filter((c): c is ReactElement => isValidElement(c) && isElementType(c, 'tr'));
  };

  // Helper to process standalone tr elements
  const processTr = (
    child: ReactElement,
    headerCells: string[]
  ): { headerCells: string[]; isDataRow: boolean } => {
    const cells = Children.toArray((child.props as { children?: React.ReactNode }).children);

    if (headerCells.length === 0 && isHeaderRow(cells)) {
      return { headerCells: extractHeaderCells(cells), isDataRow: false };
    }

    return { headerCells: [], isDataRow: true };
  };

  const { columns, rows } = useMemo(() => {
    const childArray = Children.toArray(children);
    let headerCells: string[] = [];
    let dataRows: ReactElement[] = [];

    // Extract header and body rows
    childArray.forEach((child) => {
      if (!isValidElement(child)) {
        return;
      }

      if (isElementType(child, 'thead')) {
        headerCells = processThead(child);
      } else if (isElementType(child, 'tbody')) {
        dataRows = processTbody(child);
      } else if (isElementType(child, 'tr')) {
        const result = processTr(child, headerCells);

        if (result.headerCells.length > 0) {
          headerCells = result.headerCells;
        } else if (result.isDataRow) {
          dataRows.push(child);
        }
      }
    });

    // If no header found, generate generic column names
    if (headerCells.length === 0 && dataRows.length > 0) {
      const firstRow = dataRows[0];
      const cells = Children.toArray((firstRow.props as { children?: React.ReactNode }).children);
      const cellCount = cells.filter((c): c is ReactElement => isValidElement(c)).length;
      headerCells = Array.from({ length: cellCount }, (_, i) => `Column ${i + 1}`);
    }

    // Create DataGrid columns
    const gridColumns: GridColDef[] = headerCells.map((header, index) => ({
      field: `col${index}`,
      headerName: header,
      flex: 1,
      minWidth: 150,
      hideable: true,
      renderCell: (params) => (
        <div style={{ whiteSpace: 'normal', wordWrap: 'break-word', padding: '8px 0' }}>
          {(params.row[`__content_col${index}`] as ReactNode) ?? params.value}
        </div>
      ),
    }));

    // Create DataGrid rows
    const gridRows = dataRows.map((row, rowIndex) => {
      const cells = Children.toArray((row.props as { children?: React.ReactNode }).children);
      const rowData: Record<string, unknown> = { id: rowIndex };

      cells
        .filter(
          (c): c is ReactElement => isValidElement(c) && (isElementType(c, 'td') || isElementType(c, 'th'))
        )
        .forEach((cell, cellIndex) => {
          const content = (cell.props as { children?: React.ReactNode }).children;
          // Store extracted text for sorting/filtering/CSV
          rowData[`col${cellIndex}`] = extractText(content as ReactNode);
          // Store React content for visual rendering in renderCell
          rowData[`__content_col${cellIndex}`] = content ?? '';
        });

      return rowData;
    });

    return { columns: gridColumns, rows: gridRows };
  }, [children]);

  if (columns.length === 0 || rows.length === 0) {
    return null;
  }

  const hasManyRows = rows.length > 10;

  const hideFooter = !hasManyRows;
  const showQuickFilter = hasManyRows;

  return (
    <DataGridContainer>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          hideFooter={hideFooter}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 10, page: 0 },
            },
          }}
          pageSizeOptions={[5, 10, 25, 50, 100]}
          slots={{
            toolbar: GridToolbar,
          }}
          slotProps={{
            toolbar: {
              showQuickFilter,
              printOptions: { disableToolbarButton: false },
              csvOptions: { disableToolbarButton: false },
            },
          }}
          getRowHeight={() => 'auto'}
          disableRowSelectionOnClick
          disableColumnSelector={false}
          sx={{
            '& .MuiDataGrid-columnHeaderTitle': {
              fontFamily: 'var(--font-semibold)',
            },
            '& .MuiDataGrid-cell': {
              alignItems: 'flex-start',
            },
          }}
        />
      </div>
    </DataGridContainer>
  );
};

const DataGridContainer = styled.div({
  marginTop: 0,
  marginBottom: 8,
  width: '100%',
});
